/**
 * CDM GitOps for Security — Track 6C moonshot.
 *
 * Three tabs:
 *  - Source-of-truth state: tree view of firewall-policies/ with
 *    canonical checksums + drift indicators (rose when checksum mismatches
 *    declared, amber when validator errors, emerald when clean).
 *  - Open PRs: list of recent PRs that touched firewall-policies/
 *    (best-effort via gh CLI; surfaces "gh not available" gracefully).
 *  - Reconciliation queue: drift findings that need PRs. Admin can fire a
 *    refresh that calls the drift detector + opens PRs in one shot.
 */
import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import {
  GitBranch, ChevronLeft, RefreshCw, FileText, AlertTriangle,
  CheckCircle2, GitPullRequest, ExternalLink, Lock,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

interface GitopsRow {
  path: string
  kind: string
  source: string
  source_id: string
  owner: string | null
  tier: string | null
  compliance_tags: string[]
  checksum_canonical: string
  checksum_declared: string
  checksum_matches: boolean
  errors: string[]
}

interface GitopsState {
  repo_root: string
  count: number
  rows: GitopsRow[]
}

interface DriftFinding {
  path: string
  source: string
  source_id: string
  git_checksum: string
  live_checksum: string
  suggestion: string
  summary: string
  diff_fields: string[]
  pr_url: string | null
  skipped: string | null
}

interface DriftSummary {
  scanned: number
  drifted: number
  prs_opened: number
  prs_skipped: number
  findings: DriftFinding[]
}

interface PRRow {
  number: number
  title: string
  url: string
  state: string
  updatedAt: string
  author: { login: string }
}

interface PRPayload {
  available: boolean
  rows: PRRow[]
  note?: string
}

type Tab = 'state' | 'prs' | 'drift'

export default function CdmGitopsPage() {
  const [tab, setTab] = useState<Tab>('state')
  const [state, setState] = useState<GitopsState | null>(null)
  const [drift, setDrift] = useState<DriftSummary | null>(null)
  const [prs, setPrs] = useState<PRPayload | null>(null)
  const [loading, setLoading] = useState(false)
  const [busy, setBusy] = useState<string | null>(null)
  const [message, setMessage] = useState('')

  const load = () => {
    setLoading(true)
    Promise.all([
      api.get('/api/cdm/gitops/state'),
      api.get('/api/cdm/gitops/drift?open_prs=false'),
      api.get('/api/cdm/gitops/prs?limit=20'),
    ])
      .then(([s, d, p]) => {
        setState(s.data || null)
        setDrift(d.data || null)
        setPrs(p.data || null)
      })
      .catch(err => setMessage(`Load failed: ${err.response?.data?.detail || err.message}`))
      .finally(() => setLoading(false))
  }
  useEffect(() => { load() }, [])

  const refreshDrift = async () => {
    setBusy('drift')
    try {
      const { data } = await api.get('/api/cdm/gitops/drift?open_prs=true')
      setDrift(data || null)
      setMessage(
        `Drift scan: scanned=${data.scanned} drifted=${data.drifted} ` +
        `prs_opened=${data.prs_opened} prs_skipped=${data.prs_skipped}`,
      )
    } catch (e: any) {
      setMessage(`Drift refresh failed: ${e.response?.data?.detail || e.message}`)
    } finally {
      setBusy(null)
    }
  }

  const tree = useMemo(() => {
    const rows = state?.rows || []
    const grouped: Record<string, GitopsRow[]> = {}
    for (const r of rows) {
      const top = r.path.split('/').slice(0, 2).join('/') || 'firewall-policies'
      ;(grouped[top] = grouped[top] || []).push(r)
    }
    return Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b))
  }, [state])

  return (
    <div className="space-y-4 animate-fade-in">
      <div className="flex items-baseline gap-4 pb-2 border-b border-border flex-wrap">
        <Link to="/app/cdm" className="text-text-muted hover:text-text-primary inline-flex items-center gap-1">
          <ChevronLeft size={14} /> CDM
        </Link>
        <h1 className="text-lg font-bold text-text-primary flex items-center gap-2">
          <GitBranch size={18} className="text-cyan" /> CDM GITOPS FOR SECURITY
        </h1>
        <span className="text-[11px] uppercase tracking-widest text-text-muted">
          firewall-as-code · PR risk envelope · drift PRs
        </span>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-[10px] uppercase tracking-widest border border-cyan/40 bg-cyan/10 text-cyan-light px-2 py-1 rounded inline-flex items-center gap-1">
            <Lock size={11} /> Track 6C · Phase A
          </span>
          <button type="button" onClick={load} disabled={loading} className="btn-ghost">
            <RefreshCw size={11} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>
      </div>

      {message && (
        <div className="text-[11px] text-text-secondary font-mono px-3 py-2 border border-border rounded bg-surface-card">
          {message}
        </div>
      )}

      <div className="card p-3 text-[11px] text-text-muted">
        Source-of-truth firewall policy lives in <code>firewall-policies/</code>. PRs trigger a CDM
        risk-envelope validator (predicted path-status flips + observed-flow breakages).
        Merges fire ChangeProposals on the Track 6B pipeline. Drift detector runs every 4 h and
        opens reconciliation PRs when git ≠ live. Phase A: real PATCH calls still gated by
        <code> CDM_CHANGE_PIPELINE_LIVE</code>.
      </div>

      <div className="flex items-center gap-1 border-b border-border">
        <TabButton active={tab === 'state'}  onClick={() => setTab('state')}
                   icon={FileText}        label={`Source-of-truth · ${state?.count ?? 0}`} />
        <TabButton active={tab === 'prs'}    onClick={() => setTab('prs')}
                   icon={GitPullRequest}  label={`Open PRs · ${prs?.rows?.length ?? 0}`} />
        <TabButton active={tab === 'drift'}  onClick={() => setTab('drift')}
                   icon={AlertTriangle}   label={`Reconciliation queue · ${drift?.drifted ?? 0}`} />
      </div>

      {/* Tab: source-of-truth tree */}
      {tab === 'state' && (
        <div className="space-y-3">
          {tree.length === 0 && !loading && (
            <div className="text-text-muted text-sm py-4">
              No YAML found under firewall-policies/. Add a file and re-scan.
            </div>
          )}
          {tree.map(([prefix, rows]) => (
            <div key={prefix} className="card p-0 overflow-hidden">
              <div className="px-3 py-2 border-b border-border bg-surface-raised text-[11px] uppercase tracking-widest text-text-secondary">
                {prefix} · <span className="text-text-muted">{rows.length} file{rows.length === 1 ? '' : 's'}</span>
              </div>
              <table className="w-full text-xs">
                <thead className="bg-surface-raised/50">
                  <tr className="text-[10px] uppercase tracking-widest text-text-muted border-b border-border">
                    <th className="text-left px-3 py-1.5">Path</th>
                    <th className="text-left px-3 py-1.5">Kind</th>
                    <th className="text-left px-3 py-1.5">Source ID</th>
                    <th className="text-left px-3 py-1.5">Owner</th>
                    <th className="text-left px-3 py-1.5">Checksum</th>
                    <th className="text-left px-3 py-1.5">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {rows.map(r => {
                    const tone = r.errors.length > 0
                      ? 'bg-rose/15 text-rose-light border-rose/30'
                      : r.checksum_matches
                        ? 'bg-emerald/15 text-emerald border-emerald/30'
                        : 'bg-rose/15 text-rose-light border-rose/30'
                    const label = r.errors.length > 0
                      ? 'invalid'
                      : r.checksum_matches ? 'clean' : 'checksum drift'
                    return (
                      <tr key={r.path} className="hover:bg-surface-raised/40">
                        <td className="px-3 py-1.5 font-mono text-[10px] truncate max-w-[280px]" title={r.path}>
                          {r.path}
                        </td>
                        <td className="px-3 py-1.5 font-mono text-[10px]">{r.kind}</td>
                        <td className="px-3 py-1.5 font-mono text-[10px] text-text-muted truncate max-w-[180px]" title={r.source_id}>
                          {r.source_id}
                        </td>
                        <td className="px-3 py-1.5 text-[10px] text-text-muted">{r.owner || '—'}</td>
                        <td className="px-3 py-1.5 font-mono text-[10px] text-text-muted">
                          {r.checksum_canonical.slice(0, 12)}…
                        </td>
                        <td className="px-3 py-1.5">
                          <span className={clsx('px-1.5 py-0.5 rounded border text-[9px] uppercase tracking-wider', tone)}>
                            {label}
                          </span>
                          {r.errors.length > 0 && (
                            <span className="ml-2 text-[9px] text-rose-light" title={r.errors.join('; ')}>
                              ({r.errors.length} error{r.errors.length === 1 ? '' : 's'})
                            </span>
                          )}
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {/* Tab: PRs */}
      {tab === 'prs' && (
        <div className="card p-0 overflow-hidden">
          {prs && !prs.available && (
            <div className="px-3 py-3 text-[11px] text-amber-light">
              {prs.note || 'gh CLI not available on the backend host — PR list unavailable.'}
            </div>
          )}
          <table className="w-full text-xs">
            <thead className="bg-surface-raised">
              <tr className="text-[10px] uppercase tracking-widest text-text-muted border-b border-border">
                <th className="text-left px-3 py-2">#</th>
                <th className="text-left px-3 py-2">Title</th>
                <th className="text-left px-3 py-2">State</th>
                <th className="text-left px-3 py-2">Author</th>
                <th className="text-left px-3 py-2">Updated</th>
                <th className="text-left px-3 py-2 w-px"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {(prs?.rows || []).map(p => (
                <tr key={p.number} className="hover:bg-surface-raised/40">
                  <td className="px-3 py-1.5 font-mono text-[10px]">#{p.number}</td>
                  <td className="px-3 py-1.5 truncate max-w-[400px]">{p.title}</td>
                  <td className="px-3 py-1.5 text-[10px] uppercase tracking-wider">{p.state}</td>
                  <td className="px-3 py-1.5 text-[10px] text-text-muted">{p.author?.login}</td>
                  <td className="px-3 py-1.5 text-[10px] text-text-muted">
                    {p.updatedAt ? new Date(p.updatedAt).toLocaleString() : '—'}
                  </td>
                  <td className="px-3 py-1.5 whitespace-nowrap">
                    <a href={p.url} target="_blank" rel="noopener noreferrer"
                       className="text-[10px] text-cyan-light hover:text-cyan inline-flex items-center gap-1">
                      <ExternalLink size={10} /> open
                    </a>
                  </td>
                </tr>
              ))}
              {(!prs || prs.rows?.length === 0) && (
                <tr><td colSpan={6} className="px-3 py-6 text-center text-text-muted">
                  No PRs touching firewall-policies yet.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Tab: drift / reconciliation queue */}
      {tab === 'drift' && (
        <div className="space-y-3">
          <div className="flex items-center gap-2 card p-3">
            <span className="text-[11px] text-text-muted">
              Drift findings for the firewall-policies tree vs cdm_network_rules. Admin: refresh
              opens reconciliation PRs via the gh CLI when configured.
            </span>
            <button type="button" onClick={refreshDrift} disabled={busy === 'drift'}
                    className="btn-primary text-[11px] ml-auto">
              <RefreshCw size={11} className={busy === 'drift' ? 'animate-spin' : ''} />
              Run drift + open PRs
            </button>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <Stat label="scanned"    value={drift?.scanned ?? 0} />
            <Stat label="drifted"    value={drift?.drifted ?? 0} tone={drift?.drifted ? 'rose' : 'emerald'} />
            <Stat label="prs opened" value={drift?.prs_opened ?? 0} tone="emerald" />
            <Stat label="prs skipped" value={drift?.prs_skipped ?? 0} tone={drift?.prs_skipped ? 'amber' : undefined} />
          </div>

          <div className="card p-0 overflow-hidden">
            <table className="w-full text-xs">
              <thead className="bg-surface-raised">
                <tr className="text-[10px] uppercase tracking-widest text-text-muted border-b border-border">
                  <th className="text-left px-3 py-2">Path</th>
                  <th className="text-left px-3 py-2">Source / ID</th>
                  <th className="text-left px-3 py-2">Diff fields</th>
                  <th className="text-left px-3 py-2">Suggestion</th>
                  <th className="text-left px-3 py-2">PR</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {(drift?.findings || []).map(f => (
                  <tr key={`${f.path}-${f.source_id}`} className="hover:bg-surface-raised/40">
                    <td className="px-3 py-1.5 font-mono text-[10px] truncate max-w-[260px]" title={f.path}>{f.path}</td>
                    <td className="px-3 py-1.5 font-mono text-[10px] text-text-muted">{f.source} / {f.source_id}</td>
                    <td className="px-3 py-1.5 text-[10px]">
                      {f.diff_fields.length === 0 ? (
                        <span className="text-text-muted italic">checksum-only drift</span>
                      ) : (
                        <span title={f.diff_fields.join('\n')}>{f.diff_fields.length} field{f.diff_fields.length === 1 ? '' : 's'}</span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-[10px] uppercase tracking-wider">{f.suggestion.replace(/_/g, ' ')}</td>
                    <td className="px-3 py-1.5 text-[10px]">
                      {f.pr_url ? (
                        <a href={f.pr_url} target="_blank" rel="noopener noreferrer"
                           className="text-cyan-light hover:text-cyan inline-flex items-center gap-1">
                          <ExternalLink size={10} /> view
                        </a>
                      ) : f.skipped ? (
                        <span className="text-amber-light" title={f.skipped}>skipped</span>
                      ) : (
                        <span className="text-text-muted italic">not opened</span>
                      )}
                    </td>
                  </tr>
                ))}
                {(!drift || drift.findings?.length === 0) && (
                  <tr><td colSpan={5} className="px-3 py-6 text-center text-text-muted">
                    <CheckCircle2 size={18} className="inline mr-1 text-emerald" />
                    No drift detected — git matches live.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  )
}


function TabButton({ active, onClick, icon: Icon, label }: {
  active: boolean
  onClick: () => void
  icon: React.ElementType
  label: string
}) {
  return (
    <button type="button" onClick={onClick}
            className={clsx(
              'inline-flex items-center gap-1.5 px-3 py-2 text-[11px] uppercase tracking-widest border-b-2 transition-colors',
              active ? 'border-cyan text-text-primary' : 'border-transparent text-text-muted hover:text-text-secondary',
            )}>
      <Icon size={12} /> {label}
    </button>
  )
}


function Stat({ label, value, tone }: { label: string; value: number | string; tone?: 'rose' | 'emerald' | 'amber' }) {
  const cls = tone === 'rose'    ? 'text-rose-light'
            : tone === 'emerald' ? 'text-emerald'
            : tone === 'amber'   ? 'text-amber-light'
            : 'text-text-primary'
  return (
    <div className="card p-3">
      <div className="text-[9px] uppercase tracking-widest text-text-muted">{label}</div>
      <div className={clsx('text-2xl font-mono font-bold mt-0.5', cls)}>{value}</div>
    </div>
  )
}
